/* Status bar — connector state lives HERE, not buried in a settings dialog.
 * An operator must see at a glance that DIS is live (UI-UX-DESIGN.md §3 zone F).
 * Run provenance is also surfaced: any number on screen must be traceable to a manifest.
 */
import { Activity, Database, Hexagon, Layers3, Users } from 'lucide-react'
import { useDeck } from '../state/store'
import { hms, thousands } from '../lib/format'

function Dot({ token }: { token: string }) {
  return <span className="size-1.5 rounded-full" style={{ background: token }} aria-hidden />
}

function Item({
  icon,
  label,
  value,
  title,
}: {
  icon?: React.ReactNode
  label?: string
  value: string
  title?: string
}) {
  return (
    <div className="flex items-center gap-1.5" title={title}>
      {icon}
      {label && (
        <span className="label" style={{ fontSize: 8.5 }}>
          {label}
        </span>
      )}
      <span className="num" style={{ fontSize: 10.5, color: 'var(--color-text-2)' }}>
        {value}
      </span>
    </div>
  )
}

export function StatusBar() {
  const running = useDeck((s) => s.running)
  const compression = useDeck((s) => s.compression)
  const elapsedSec = useDeck((s) => s.elapsedSec)
  const contacts = useDeck((s) => s.contacts)
  const entityCount = useDeck((s) => s.entities.length)
  const connection = useDeck((s) => s.connection)
  const simTick = useDeck((s) => s.simTick)
  const seed = useDeck((s) => s.seed)
  const engineVersion = useDeck((s) => s.engineVersion)
  const behindTicks = useDeck((s) => s.behindTicks)
  const datasetRef = useDeck((s) => s.datasetRef)
  const scenarioDigest = useDeck((s) => s.scenarioDigest)
  const bundle = useDeck((s) => s.bundle)
  const me = useDeck((s) => s.me)

  // Whether the numbers on screen come from the kernel or the seeded fixture. Stated, never
  // implied: a demo that has silently fallen back to fixture data looks exactly like a working
  // one until somebody asks.
  const live = connection === 'live'
  const coreLabel =
    connection === 'live' ? 'LIVE CORE' : connection === 'connecting' ? 'CONNECTING' : 'FIXTURE'
  const coreToken =
    connection === 'live'
      ? 'var(--color-ok)'
      : connection === 'connecting'
        ? 'var(--color-warn)'
        : 'var(--color-idle)'

  return (
    <footer
      className="flex h-6 shrink-0 items-center gap-3 border-t px-3"
      style={{ background: 'var(--color-surface-1)', borderColor: 'var(--color-line-2)' }}
    >
      {/* Side + role */}
      <div className="flex items-center gap-1.5">
        <Dot token="var(--color-friend)" />
        <span className="label" style={{ fontSize: 8.5, color: 'var(--color-friend)' }}>
          BLUE
        </span>
        <span style={{ fontSize: 9, color: 'var(--color-text-3)' }}>·</span>
        {/* The role the GATEWAY reports, not one the client assumed. If the server disagrees with
            what this bar says, the bar is wrong and the server wins — so it shows the server's
            answer directly. */}
        <span
          className="label"
          style={{ fontSize: 8.5 }}
          title={
            me
              ? `${me.user} — role ${me.role}${me.side ? `, side ${me.side}` : ', all sides'}. ` +
                `${me.mayWrite ? 'May issue orders' : 'Read-only'}; ` +
                `${me.mayControlTime ? 'may control the clock' : 'clock is EXCON-controlled'}.`
              : 'Not authenticated — showing fixture data'
          }
        >
          {me ? me.role.toUpperCase() : 'CDR'}
        </span>
      </div>

      <div className="h-3 w-px" style={{ background: 'var(--color-line-2)' }} />

      {/* Counts must agree with the connection state. Showing the fixture's 412 units beside a
          LIVE CORE badge is exactly the mixed-truth display the badge exists to prevent — the
          operator would have no way to tell which numbers came from where. */}
      <Item
        icon={<Layers3 size={11} style={{ color: 'var(--color-text-3)' }} />}
        label="Units"
        value={String(entityCount)}
        title={live ? 'Entities in the live scenario' : 'Fixture count — not a live run'}
      />
      {/* Now that sensing exists this IS the live count. It was "—" while the kernel held no
          contacts, which was the honest answer then and would be a lie now. */}
      <Item
        label="Contacts"
        value={String(contacts.length)}
        title={
          live
            ? "Tracks in BLUE's picture — sensor-derived estimates, not truth"
            : 'Fixture count — not a live run'
        }
      />
      <Item
        label="Shown"
        value={String(contacts.length)}
        title="Contact symbols currently drawn"
      />

      <div className="h-3 w-px" style={{ background: 'var(--color-line-2)' }} />

      <Item icon={<Activity size={11} style={{ color: running ? 'var(--color-ok)' : 'var(--color-idle)' }} />} label="Sim" value={running ? `×${compression}` : 'PAUSED'} />
      <Item label="Elapsed" value={hms(elapsedSec)} />
      {live && <Item label="Tick" value={thousands(simTick)} title="Kernel tick — integer, authoritative" />}
      {behindTicks > 0 && (
        <span
          className="num"
          style={{ fontSize: 10, color: 'var(--color-warn)' }}
          title="The kernel could not keep up with the requested time compression. The simulation is running slower than the factor shown."
        >
          −{thousands(behindTicks)} behind
        </span>
      )}

      <div className="h-3 w-px" style={{ background: 'var(--color-line-2)' }} />

      {/* Is this real? The single most important thing on this bar. */}
      <div
        className="flex items-center gap-1.5"
        title={
          connection === 'live'
            ? `Simulation state is coming from the ORBIS Core kernel (engine ${engineVersion ?? '?'}). Positions, courses and fuel are computed, not scripted.`
            : connection === 'connecting'
              ? 'Connecting to the Gateway. Showing seeded fixture data until the first snapshot arrives.'
              : 'Gateway unreachable — showing SEEDED FIXTURE DATA. Nothing on screen is being simulated.'
        }
      >
        <Dot token={coreToken} />
        <span className="label" style={{ fontSize: 8.5, color: coreToken }}>
          {coreLabel}
        </span>
      </div>

      {/* Interop connectors — visible at a glance */}
      <div className="ml-auto flex items-center gap-3">
        <div className="flex items-center gap-1.5" title="DIS connector — joined, multicast 239.1.2.3:3000">
          <Dot token="var(--color-ok)" />
          <span className="label" style={{ fontSize: 8.5 }}>
            DIS
          </span>
          <span className="num" style={{ fontSize: 10, color: 'var(--color-text-3)' }}>
            1.8k/s
          </span>
        </div>

        <div className="flex items-center gap-1.5" title="HLA RTI — not connected in prototype">
          <Dot token="var(--color-idle)" />
          <span className="label" style={{ fontSize: 8.5 }}>
            HLA
          </span>
        </div>

        <div className="flex items-center gap-1.5" title="7 federate entities have no standards_mapping row">
          <Dot token="var(--color-warn)" />
          <span className="label" style={{ fontSize: 8.5, color: 'var(--color-warn)' }}>
            7 unmapped
          </span>
        </div>

        <div className="h-3 w-px" style={{ background: 'var(--color-line-2)' }} />

        {/* Run provenance — reproducibility is visible, not buried */}
        <Item
          icon={<Database size={11} style={{ color: 'var(--color-text-3)' }} />}
          label="Dataset"
          value={datasetRef ? datasetRef.split('@')[1] : '—'}
          title={
            live && datasetRef
              ? `Dataset ${datasetRef} — content-addressed and pinned by this scenario. Any edit to the data produces a different digest, so this identifies the exact platform figures behind every number on screen.`
              : `Fixture digest — not a live run`
          }
        />
        <Item
          icon={<Hexagon size={11} style={{ color: 'var(--color-text-3)' }} />}
          label="Bundle"
          value={bundle ? bundle.digest.slice(0, 8) : '—'}
          title={
            bundle
              ? `Geospatial bundle ${bundle.name} — digest ${bundle.digest}, ${
                  Object.keys(bundle.layers).length
                } layers, staged ${bundle.createdIso}, covering ${bundle.aoi.west}..${
                  bundle.aoi.east
                }E / ${bundle.aoi.south}..${bundle.aoi.north}N at zoom ${bundle.zoomMin}-${
                  bundle.zoomMax
                }. Every tile on screen came from it; nothing was fetched.`
              : 'No staged geospatial bundle on this deployment — the map is inline placeholder geometry. Stage one with `python -m orbis_atlas stage`.'
          }
        />
        {live && scenarioDigest && (
          <Item
            label="Scenario"
            value={scenarioDigest.slice(0, 8)}
            title={`Scenario document digest ${scenarioDigest} — the placements, sides and doctrine that produced this run. Distinct from the dataset digest: one names the situation, the other names the platform data.`}
          />
        )}
        <Item
          label="Seed"
          value={seed ?? '—'}
          title={
            live
              ? `Root RNG seed ${seed} — this run is bit-reproducible from it`
              : 'Fixture seed — not a live run'
          }
        />

        <div className="h-3 w-px" style={{ background: 'var(--color-line-2)' }} />

        <div className="flex items-center gap-1.5" title="Connected operators">
          <Users size={11} style={{ color: 'var(--color-text-3)' }} aria-hidden />
          <span className="num" style={{ fontSize: 10.5, color: 'var(--color-text-2)' }}>
            4
          </span>
        </div>
      </div>
    </footer>
  )
}
